import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import type {
  AdvanceExpenseCategory,
  AdvancePrecheckResult,
  AdvanceReceiptUploadResult,
  AdvanceStatus,
  BulkRemindResultRow,
  ClearEmployeeAdvance,
  CreateEmployeeAdvance,
  EmployeeAdvanceListRow,
  EmployeeAdvanceWithApprovals,
  EmployeeAdvanceWithClearances,
  PayrollDeductionRow,
  PerDiemRate,
  RemindAdvance,
  RemindAdvanceResult,
  TaxablePerDiemRow,
  WriteOffEmployeeAdvance,
} from '@dcc-erp/shared';
import {
  AdvanceExpenseCategorySchema,
  AdvancePrecheckResultSchema,
  AdvanceReceiptUploadResultSchema,
  BulkRemindResultRowSchema,
  PayrollDeductionRowSchema,
  PerDiemRateSchema,
  TaxablePerDiemRowSchema,
} from '@dcc-erp/shared';

import { api, apiJson, downloadFile, withSodOverride } from '../api';

// Cache-key scope — keyed by companyId so DCC/TTOP/RCI switches give
// independent caches (same pattern as queries/ar.ts and queries/lva.ts).
const advancesKey = (companyId: string | undefined) => ['advances', companyId] as const;

export interface AdvanceFilters {
  employeeId?: string;
  status?: AdvanceStatus;
  dateFrom?: string;
  dateTo?: string;
  overdue?: boolean;
}

export function useAdvances(companyId: string | undefined, filters: AdvanceFilters = {}) {
  const qs = new URLSearchParams();
  if (filters.employeeId) qs.set('employeeId', filters.employeeId);
  if (filters.status) qs.set('status', filters.status);
  if (filters.dateFrom) qs.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) qs.set('dateTo', filters.dateTo);
  if (filters.overdue) qs.set('overdue', 'true');
  const search = qs.toString();
  return useQuery({
    queryKey: [...advancesKey(companyId), filters],
    queryFn: () =>
      api<EmployeeAdvanceListRow[]>(
        `/companies/${companyId}/advances${search ? `?${search}` : ''}`,
      ),
    enabled: !!companyId,
  });
}

export function useAdvance(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: ['advances', companyId, id],
    queryFn: () => api<EmployeeAdvanceWithApprovals>(`/companies/${companyId}/advances/${id}`),
    enabled: !!companyId && !!id,
  });
}

// M3 — tax-treatment master driving the clearance modal's per-line VAT/WHT
// defaults. Company-scoped GET (mirrors useAdvances/useAdvance via `api`); parsed
// through the shared schema so whtRate Decimal strings coerce to numbers.
export function useAdvanceExpenseCategories(companyId: string | undefined) {
  return useQuery({
    queryKey: ['advance-expense-categories', companyId],
    queryFn: async (): Promise<AdvanceExpenseCategory[]> => {
      const rows = await api<unknown>(`/companies/${companyId}/advances/expense-categories`);
      return z.array(AdvanceExpenseCategorySchema).parse(rows);
    },
    enabled: !!companyId,
    staleTime: 5 * 60_000,
  });
}

// M5 — per-diem / mileage allowance master driving the clearance modal's
// PER_DIEM/MILEAGE lines. Company-scoped GET (mirrors useAdvanceExpenseCategories
// via `api`); parsed through the shared schema so the rate/ceilingRate Decimal
// strings coerce to numbers.
export function usePerDiemRates(companyId: string | undefined) {
  return useQuery({
    queryKey: ['advance-perdiem-rates', companyId],
    queryFn: async (): Promise<PerDiemRate[]> => {
      const rows = await api<unknown>(`/companies/${companyId}/advances/perdiem-rates`);
      return z.array(PerDiemRateSchema).parse(rows);
    },
    enabled: !!companyId,
    staleTime: 5 * 60_000,
  });
}

// M5 — report-only payroll-deduction surface. Lists the advances whose write-off
// flagged a residual the employee still owes (HR deducts out-of-band, no JV).
// Parsed through the shared schema (pendingPayrollAmount Decimal coerces).
export function usePayrollDeductions(companyId: string | undefined) {
  return useQuery({
    queryKey: ['advance-payroll-deductions', companyId],
    queryFn: async (): Promise<PayrollDeductionRow[]> => {
      const rows = await api<unknown>(`/companies/${companyId}/advances/payroll-deductions`);
      return z.array(PayrollDeductionRowSchema).parse(rows);
    },
    enabled: !!companyId,
  });
}

// M5 — report-only taxable per-diem surface (FIX 5). Lists the PER_DIEM/MILEAGE
// clearance lines whose claimed rate exceeded the civil-service ceiling
// (taxablePerDiemExcess > 0) so HR can pick it up for ภ.ง.ด.1 (the excess is
// flagged at clearance but NOT withheld). Parsed through the shared schema
// (taxablePerDiemExcess Decimal coerces).
export function usePerDiemTaxable(companyId: string | undefined) {
  return useQuery({
    queryKey: ['advance-taxable-perdiem', companyId],
    queryFn: async (): Promise<TaxablePerDiemRow[]> => {
      const rows = await api<unknown>(`/companies/${companyId}/advances/taxable-perdiem`);
      return z.array(TaxablePerDiemRowSchema).parse(rows);
    },
    enabled: !!companyId,
  });
}

export function useCreateAdvance(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      data,
      idempotencyKey,
    }: {
      data: CreateEmployeeAdvance;
      idempotencyKey?: string;
    }) =>
      api<EmployeeAdvanceListRow>(`/companies/${companyId}/advances`, {
        method: 'POST',
        headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: advancesKey(companyId) });
      // The issuance posts a JV — refresh the GL view too.
      void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
    },
  });
}

export function useClearAdvance(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
      idempotencyKey,
    }: {
      id: string;
      data: ClearEmployeeAdvance;
      idempotencyKey?: string;
    }) =>
      api<EmployeeAdvanceWithClearances>(`/companies/${companyId}/advances/${id}/clear`, {
        method: 'POST',
        headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: advancesKey(companyId) });
      void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
    },
  });
}

export function useWriteOffAdvance(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
      idempotencyKey,
    }: {
      id: string;
      data: WriteOffEmployeeAdvance;
      idempotencyKey?: string;
    }) =>
      api<EmployeeAdvanceWithClearances>(`/companies/${companyId}/advances/${id}/write-off`, {
        method: 'POST',
        headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: advancesKey(companyId) });
      void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
    },
  });
}

// ---- M2 central-engine approval + disburse mutations ----

function invalidateAdvanceEngine(
  qc: ReturnType<typeof useQueryClient>,
  companyId: string | undefined,
) {
  void qc.invalidateQueries({ queryKey: advancesKey(companyId) });
  void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
}

export function useSubmitAdvance(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiJson<undefined, EmployeeAdvanceWithApprovals>(
        'POST',
        `/companies/${companyId}/advances/${id}/submit`,
      ),
    onSuccess: () => invalidateAdvanceEngine(qc, companyId),
  });
}

export function useApproveAdvanceStep(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      step,
      comment,
      sodOverrideReason,
    }: {
      id: string;
      step: number;
      comment?: string;
      sodOverrideReason?: string;
    }) =>
      apiJson<
        { step: number; comment?: string; sodOverrideReason?: string },
        EmployeeAdvanceWithApprovals
      >(
        'POST',
        `/companies/${companyId}/advances/${id}/approve-step`,
        withSodOverride({ step, ...(comment !== undefined ? { comment } : {}) }, sodOverrideReason),
      ),
    onSuccess: () => invalidateAdvanceEngine(qc, companyId),
  });
}

export function useRejectAdvanceStep(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      step,
      reason,
      sodOverrideReason,
    }: {
      id: string;
      step: number;
      reason: string;
      sodOverrideReason?: string;
    }) =>
      apiJson<
        { step: number; reason: string; sodOverrideReason?: string },
        EmployeeAdvanceWithApprovals
      >(
        'POST',
        `/companies/${companyId}/advances/${id}/reject-step`,
        withSodOverride({ step, reason }, sodOverrideReason),
      ),
    onSuccess: () => invalidateAdvanceEngine(qc, companyId),
  });
}

export function useReturnAdvanceStep(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      step,
      reason,
      sodOverrideReason,
    }: {
      id: string;
      step: number;
      reason: string;
      sodOverrideReason?: string;
    }) =>
      apiJson<
        { step: number; reason: string; sodOverrideReason?: string },
        EmployeeAdvanceWithApprovals
      >(
        'POST',
        `/companies/${companyId}/advances/${id}/return`,
        withSodOverride({ step, reason }, sodOverrideReason),
      ),
    onSuccess: () => invalidateAdvanceEngine(qc, companyId),
  });
}

export function useDisburseAdvance(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payMethod,
      disburseAccountCode,
    }: {
      id: string;
      payMethod?: string;
      disburseAccountCode?: string;
    }) =>
      apiJson<{ payMethod?: string; disburseAccountCode?: string }, EmployeeAdvanceWithApprovals>(
        'POST',
        `/companies/${companyId}/advances/${id}/disburse`,
        {
          ...(payMethod ? { payMethod } : {}),
          ...(disburseAccountCode ? { disburseAccountCode } : {}),
        },
      ),
    onSuccess: () => invalidateAdvanceEngine(qc, companyId),
  });
}

// ---- M4: receipt upload + OCR prefill, soft precheck, manual remind ----

// POST :id/receipts (multipart) — upload a clearance receipt to MinIO, run Claude
// OCR, return { storageKey, fileName, suggested }. Mirrors useUploadIngest: build a
// FormData with field `file`, hand it straight to api() which lets the browser set
// the multipart boundary (no Content-Type override). The result is parsed through
// the shared schema so the `suggested` numbers coerce cleanly before prefill.
export function useUploadAdvanceReceipt(companyId: string | undefined) {
  return useMutation({
    mutationFn: async ({
      id,
      file,
    }: {
      id: string;
      file: File;
    }): Promise<AdvanceReceiptUploadResult> => {
      const form = new FormData();
      form.append('file', file, file.name);
      const raw = await api<unknown>(`/companies/${companyId}/advances/${id}/receipts`, {
        method: 'POST',
        body: form,
      });
      return AdvanceReceiptUploadResultSchema.parse(raw);
    },
  });
}

// GET advance-precheck?employeeId=&amount= — soft, warn-only create precheck. Only
// runs once an employee is picked and a positive amount is entered; the page renders
// a non-blocking banner from the result but never disables submit. Parsed through the
// shared schema (coerces the advanceLimit Decimal string). keepPreviousData so the
// banner doesn't flicker while the amount is being typed.
export function useAdvancePrecheck(
  companyId: string | undefined,
  employeeId: string | undefined,
  amount: number,
) {
  return useQuery({
    queryKey: ['advance-precheck', companyId, employeeId, amount],
    queryFn: async (): Promise<AdvancePrecheckResult> => {
      const qs = new URLSearchParams({ employeeId: employeeId ?? '', amount: String(amount) });
      const raw = await api<unknown>(
        `/companies/${companyId}/advances/advance-precheck?${qs.toString()}`,
      );
      return AdvancePrecheckResultSchema.parse(raw);
    },
    enabled: !!companyId && !!employeeId && amount > 0,
    placeholderData: (prev) => prev,
  });
}

// POST :id/remind — fire an overdue reminder through the (stub) mailer. Always-on
// (the dunning cron is flag-gated); wires the "ส่งเตือนทาง Email" button.
export function useRemindAdvance(companyId: string | undefined) {
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data?: RemindAdvance }) =>
      apiJson<RemindAdvance, RemindAdvanceResult>(
        'POST',
        `/companies/${companyId}/advances/${id}/remind`,
        data ?? {},
      ),
  });
}

// M5 — POST bulk-remind: fan out the manual overdue reminder over the selected
// advance ids. The server collects a per-id result (one non-overdue/settled id
// never aborts the batch), so the result is an array of {id, sent?, to?, error?}.
// Parsed through the shared row schema. Mirrors useRemindAdvance's apiJson style.
export function useBulkRemind(companyId: string | undefined) {
  return useMutation({
    mutationFn: async ({ ids }: { ids: string[] }): Promise<BulkRemindResultRow[]> => {
      const raw = await apiJson<{ ids: string[] }, unknown>(
        'POST',
        `/companies/${companyId}/advances/bulk-remind`,
        { ids },
      );
      return z.array(BulkRemindResultRowSchema).parse(raw);
    },
  });
}

// M5 — POST :id/payroll-deduction/clear: HR marks a flagged advance's payroll
// deduction as deducted (clears the pendingPayrollDeduction flag; no JV). Refreshes
// the payroll-deductions report so the cleared row drops out.
export function useClearPayrollDeduction(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiJson<undefined, EmployeeAdvanceWithClearances>(
        'POST',
        `/companies/${companyId}/advances/${id}/payroll-deduction/clear`,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['advance-payroll-deductions', companyId] });
      void qc.invalidateQueries({ queryKey: advancesKey(companyId) });
    },
  });
}

// Open a clearance-line receipt: resolve the short-lived signed URL server-side
// (re-scoped by companyId via line→clearance→advance) then open it in a new tab.
export async function openAdvanceReceipt(
  companyId: string | undefined,
  lineId: string,
): Promise<void> {
  const { url } = await api<{ url: string }>(
    `/companies/${companyId}/advances/clearance-lines/${lineId}/receipt-url`,
  );
  if (typeof window !== 'undefined') window.open(url, '_blank', 'noopener');
}

// Print the disbursement voucher (ใบสำคัญเบิกเงินทดรองจ่าย) — an authenticated
// blob download (the bearer token can't ride on a bare <a href>), via downloadFile.
export function printAdvanceVoucher(companyId: string | undefined, id: string): Promise<void> {
  return downloadFile(`/companies/${companyId}/advances/${id}/voucher.pdf`, `advance-${id}.pdf`);
}
